// The match state machine: server events in, renderable state out.
//
// This is the whole game client. It holds no rules — no scoring, no word
// validation, no turn order, no timing authority. Every one of those lives on
// the server, and this file's job is to be a faithful projection of what the
// server last said, plus the seven statements this client is allowed to make
// (protocol/encode.ts).
//
// Two consequences that shape everything below:
//
//   - DEADLINES ARE WALL-CLOCK INSTANTS, not durations. The server sends
//     UnixMilli, and every countdown derives its seconds from `deadline - now`
//     on each tick. A suspended WebView therefore resumes on the RIGHT number
//     instead of a drifted one, which a decrementing counter could never do.
//
//   - RESYNC RE-READS, NEVER REPLAYS. opCode 2 carries the entire match state
//     and the server sends it for a rejoin exactly as for a first join, so
//     recovery is "apply the snapshot", not "re-send what I had typed".

import * as encode from '../protocol/encode';
import type { MatchSender } from '../protocol/encode';
import type {
  MatchEndReward,
  MatchEvent,
  MatchStateSnapshot,
  PresenceSummary,
  RoundResults,
} from '../protocol/messages';
import { Store } from './store';

/** Which screen the player is looking at. Derived only from server state. */
export type Phase =
  | 'connecting'
  | 'lobby'
  | 'selectingLetter'
  | 'waitingForLetter'
  | 'round'
  | 'scoring'
  | 'results'
  | 'ended'
  | 'rejected'
  | 'error';

export interface MatchViewState {
  readonly phase: Phase;
  /** True while the reconnect loop is running — drives the blocking overlay. */
  readonly reconnecting: boolean;
  readonly error: string | null;

  readonly alias: string;
  readonly categories: readonly string[];
  readonly totalRounds: number;
  readonly currentRound: number;
  readonly alphabet: string | null;
  readonly hostUserId: string;
  readonly roster: Readonly<Record<string, PresenceSummary>>;
  /** Everyone who has EVER been in this match, for the ranking table. */
  readonly everSeen: Readonly<Record<string, PresenceSummary>>;
  readonly offline: readonly string[];
  readonly canStart: boolean;

  /** UnixMilli, or null when nothing is counting down. */
  readonly answersDeadline: number | null;
  readonly nextRoundDeadline: number | null;
  readonly isFinalRound: boolean;

  /** This player's current answers, by category. */
  readonly answers: Readonly<Record<string, string>>;
  readonly submitted: boolean;

  readonly results: RoundResults | null;
  readonly reward: MatchEndReward | null;
  /** Running total across the match, for the host score report. */
  readonly totalScore: number;

  readonly letterOptions: readonly string[];
  readonly usedLetters: readonly string[];
  readonly pickerUsername: string | null;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const initialState: MatchViewState = {
  phase: 'connecting',
  reconnecting: false,
  error: null,
  alias: '',
  categories: [],
  totalRounds: 0,
  currentRound: 0,
  alphabet: null,
  hostUserId: '',
  roster: {},
  everSeen: {},
  offline: [],
  canStart: false,
  answersDeadline: null,
  nextRoundDeadline: null,
  isFinalRound: false,
  answers: {},
  submitted: false,
  results: null,
  reward: null,
  totalScore: 0,
  letterOptions: ALPHABET,
  usedLetters: [],
  pickerUsername: null,
};

export interface MatchControllerDeps {
  readonly tx: MatchSender;
  /** This client's Nakama user id. */
  readonly userId: () => string;
  /** Fired once per match end, with the score to report to the host. */
  readonly onMatchEnd: (reward: MatchEndReward, matchId: string) => void;
  readonly onAnalytics?: (event: string, props?: Record<string, string | number | boolean>) => void;
}

export class MatchController {
  readonly store = new Store<MatchViewState>(initialState);

  /**
   * Rounds this client has already acknowledged.
   *
   * The server resends results to a reconnecting player, so `handle` can see
   * the same round's results twice. Acknowledging twice is harmless to the
   * server but would fire the analytics event twice and re-arm the reveal, so
   * the round number is tracked instead of a boolean.
   */
  private acknowledgedRounds = new Set<number>();

  constructor(private readonly deps: MatchControllerDeps) {}

  private get s(): MatchViewState {
    return this.store.get();
  }

  private track(event: string, props?: Record<string, string | number | boolean>): void {
    this.deps.onAnalytics?.(event, props);
  }

  // --- inbound ---------------------------------------------------------------

  /** Applies one server event. The only way state changes. */
  handle(event: MatchEvent): void {
    switch (event.kind) {
      case 'joinedMatch':
        this.applySnapshot(event.state);
        break;

      case 'newUserJoined':
        this.store.set({
          roster: { ...this.s.roster, [event.user.userId]: event.user },
          everSeen: { ...this.s.everSeen, [event.user.userId]: event.user },
          offline: this.s.offline.filter((id) => id !== event.user.userId),
        });
        break;

      case 'allowGameStart':
        this.store.set({ canStart: true });
        break;

      // The server sends an empty payload for full, locked and already-started
      // alike, so there is one message for all three.
      case 'matchFull':
        this.store.set({ phase: 'rejected' });
        break;

      case 'selectAlphabetRequest':
        this.store.set({
          phase: 'selectingLetter',
          currentRound: event.currentRound,
          pickerUsername: null,
        });
        this.track('nouns.lobby.viewed', { screen: 'letter_select' });
        break;

      case 'userToSelectNextAlphabet':
      case 'systemSelectAlphabet':
        // Only move screens if this client is NOT the picker; the picker is
        // already on the select screen and must not be bounced off it.
        this.store.set({
          pickerUsername: event.username,
          phase: event.userId === this.deps.userId() ? this.s.phase : 'waitingForLetter',
        });
        break;

      case 'newRoundBegin':
        this.acknowledgedRounds.delete(this.s.currentRound + 1);
        this.store.set({
          phase: 'round',
          alphabet: event.alphabet,
          answersDeadline: event.answersDeadline,
          usedLetters: [...this.s.usedLetters, event.alphabet],
          answers: {},
          submitted: false,
          results: null,
          nextRoundDeadline: null,
          pickerUsername: null,
        });
        this.track('nouns.round.started', { round: this.s.currentRound, letter: event.alphabet });
        break;

      case 'stopRound':
        // Someone ended the round early. Submit whatever is typed rather than
        // discarding it — the server is about to score this round either way.
        this.submitAnswers('stop');
        this.store.set({ phase: 'scoring' });
        break;

      case 'answersAcknowledged':
        this.store.set({ submitted: true, phase: 'scoring' });
        break;

      case 'roundResults':
        this.applyResults(event.data);
        break;

      // The deadline MOVED (fast advance, once every online human acked).
      // Published rather than inferred, so follow it.
      case 'nextRoundDeadline':
        this.store.set({
          nextRoundDeadline: event.deadlineMs,
          isFinalRound: event.isFinalRound || this.s.isFinalRound,
        });
        break;

      case 'matchEnd':
        this.applyMatchEnd(event.reward);
        break;

      case 'playerDisconnected':
        this.store.set({
          offline: this.s.offline.includes(event.user.userId)
            ? this.s.offline
            : [...this.s.offline, event.user.userId],
          everSeen: { ...this.s.everSeen, [event.user.userId]: event.user },
        });
        break;

      case 'playerReconnected':
        this.store.set({ offline: this.s.offline.filter((id) => id !== event.user.userId) });
        break;

      case 'playerKickedOut':
        if (event.userId === this.deps.userId()) {
          // The server gave up on us; the seat is gone. Nothing to resync to.
          this.store.set({ phase: 'error', error: 'kicked', reconnecting: false });
        } else {
          const { [event.userId]: _removed, ...rest } = this.s.roster;
          this.store.set({
            roster: rest,
            offline: this.s.offline.filter((id) => id !== event.userId),
          });
        }
        break;

      case 'hostUpdate':
        this.store.set({ hostUserId: event.newHostUserId });
        break;
    }
  }

  /**
   * Applies the full snapshot from opCode 2 — a first join AND every resync.
   *
   * Derives the phase from state rather than remembering where the player was,
   * which is what makes a rejoin land on the right screen without any
   * client-side bookkeeping to get out of step.
   */
  private applySnapshot(snap: MatchStateSnapshot): void {
    const everSeen = { ...this.s.everSeen, ...snap.presencesSummary };

    this.store.set({
      phase: phaseFromSnapshot(snap, this.deps.userId()),
      reconnecting: false,
      error: null,
      alias: snap.alias,
      categories: snap.categories,
      totalRounds: snap.rounds,
      currentRound: snap.currentRound,
      alphabet: snap.currentAlphabet,
      hostUserId: snap.host,
      roster: snap.presencesSummary,
      everSeen,
      // A snapshot is authoritative about who is present; anyone it does not
      // list is gone, not offline. Keeping a stale offline list here is how a
      // resync ends up showing a spinner over a player who left an hour ago.
      offline: [],
      canStart: this.s.canStart || Object.keys(snap.presencesSummary).length >= 2,
      answersDeadline: snap.answersDeadline,
      usedLetters: snap.selectedAlphabets,
      pickerUsername: snap.userToSelectAlphabet?.username ?? null,
      // Local input is DISCARDED on resync, deliberately. The round it
      // belonged to has very likely been scored already; re-showing it would
      // invite the player to resubmit into a round that no longer exists.
      answers: {},
      submitted: false,
    });

    encode.acknowledgeJoin(this.deps.tx);
    this.track('nouns.match.joined', { rejoining: snap.rejoining, round: snap.currentRound });
  }

  private applyResults(data: RoundResults): void {
    const mine = data.ranking.find((r) => r.userId === this.deps.userId());

    this.store.set({
      phase: 'results',
      results: data,
      nextRoundDeadline: data.nextRoundDeadline,
      isFinalRound: data.isFinalRound,
      // The ranking's score is CUMULATIVE for the match, so it is assigned,
      // never added — accumulating it would multiply a rider's score by the
      // round number, and a resend of the same results would inflate it again.
      totalScore: mine?.score ?? this.s.totalScore,
    });

    this.track('nouns.results.viewed', { round: this.s.currentRound });

    if (!this.acknowledgedRounds.has(this.s.currentRound)) {
      this.acknowledgedRounds.add(this.s.currentRound);
      encode.acknowledgeResults(this.deps.tx, this.deps.userId(), this.s.currentRound);
    }
  }

  private applyMatchEnd(reward: MatchEndReward): void {
    // `rank === 0` is NO SIGNAL (an empty payload from a legacy server, or the
    // idempotent replay once rewards are already granted) — never a loss.
    this.store.set({ phase: 'ended', reward });
    this.track('nouns.match.ended', { rank: reward.rank, score: this.s.totalScore });
    // A server-sent score WINS, including a zero — that zero is the server
    // saying this match earns no standing (private, early end, or no human
    // opponent), and the ranked-credit rules exist precisely to exclude it.
    // The cumulative match score is the fallback ONLY when the key is absent,
    // i.e. a server that predates the partner leaderboard.
    this.deps.onMatchEnd(
      { ...reward, score: reward.score ?? this.s.totalScore },
      this.s.alias,
    );
  }

  // --- outbound --------------------------------------------------------------

  setAnswer(category: string, value: string): void {
    this.store.set({ answers: { ...this.s.answers, [category]: value } });
  }

  /** Sends this round's answers. Idempotent enough to call from three paths. */
  submitAnswers(source: 'manual' | 'deadline' | 'stop'): void {
    if (this.s.submitted) return;
    const answers: Record<string, string> = {};
    for (const category of this.s.categories) {
      answers[category] = (this.s.answers[category] ?? '').trim();
    }
    encode.submitAnswers(this.deps.tx, answers);
    this.store.set({ submitted: true });
    this.track('nouns.round.submitted', {
      round: this.s.currentRound,
      source,
      filled: Object.values(answers).filter((a) => a.length > 0).length,
    });
  }

  selectLetter(letter: string): void {
    encode.selectAlphabet(this.deps.tx, letter);
    this.store.set({ phase: 'waitingForLetter' });
  }

  startGame(): void {
    encode.startGame(this.deps.tx);
  }

  stopRound(): void {
    encode.stopRound(this.deps.tx);
  }

  skipResults(): void {
    encode.skipResults(this.deps.tx, this.s.currentRound);
  }

  quit(): void {
    encode.quitRequest(this.deps.tx);
  }

  isHost(): boolean {
    return this.s.hostUserId === this.deps.userId();
  }

  setReconnecting(value: boolean): void {
    this.store.set({ reconnecting: value });
  }

  fail(message: string): void {
    this.store.set({ phase: 'error', error: message, reconnecting: false });
  }
}

/**
 * Derives the screen from a state snapshot.
 *
 * Order matters and follows the server's own precedence: results outrank a
 * live round (a reconnecting player whose round was scored while they were
 * away is shown the results, not an expired input form), and scoring outranks
 * both.
 */
function phaseFromSnapshot(snap: MatchStateSnapshot, myUserId: string): Phase {
  if (!snap.gameStarted) return 'lobby';
  if (snap.currentRoundScored) return 'results';
  if (snap.isScoring) return 'scoring';
  if (snap.isSelectingAlphabet) {
    return snap.userToSelectAlphabet?.userId === myUserId ? 'selectingLetter' : 'waitingForLetter';
  }
  if (snap.currentAlphabet) return 'round';
  return 'lobby';
}
